import type { CropCenterKeyframe } from '@/hooks/use-auto-save-crop-keyframes';
import { FRAME_DURATION } from '@/lib/timeline/exportFrameTiming';
import { resolveCropKeyframeAtTime } from './resolveCropKeyframeAtTime';

type IntervalLike = { startTime: number; endTime: number };

/** Stretches shorter than this are float noise at a keyframe edge, not scenes. */
const MIN_GAP_SECONDS = FRAME_DURATION / 2;

/**
 * Give every stretch of the clip's intervals that no keyframe covers its own
 * keyframe, carrying the crop the preview already holds there
 * (`resolveCropKeyframeAtTime`). The timeline can then offer framing on those
 * scenes, and an edit to one of them leaves the others where they were —
 * without the explicit keyframes, reframing one uncovered scene would also
 * move every later uncovered scene that holds it.
 *
 * Returns the input array unchanged when nothing is uncovered or there are no
 * keyframes at all (auto-crop has not produced any yet).
 */
export function fillIntervalCropGaps(
  keyframes: CropCenterKeyframe[],
  intervals: readonly IntervalLike[],
): CropCenterKeyframe[] {
  if (keyframes.length === 0) return keyframes;
  const filled: CropCenterKeyframe[] = [];
  for (const interval of intervals) {
    const covering = [...keyframes, ...filled]
      .filter((keyframe) => keyframe.endTime > interval.startTime && keyframe.time < interval.endTime)
      .sort((a, b) => a.time - b.time);
    let cursor = interval.startTime;
    const gaps: Array<[number, number]> = [];
    for (const keyframe of covering) {
      if (keyframe.time > cursor) gaps.push([cursor, Math.min(keyframe.time, interval.endTime)]);
      cursor = Math.max(cursor, keyframe.endTime);
    }
    if (cursor < interval.endTime) gaps.push([cursor, interval.endTime]);
    for (const [start, end] of gaps) {
      if (end - start < MIN_GAP_SECONDS) continue;
      const held = resolveCropKeyframeAtTime(keyframes, (start + end) / 2);
      if (!held) continue;
      filled.push({
        time: start,
        endTime: end,
        x: held.x,
        y: held.y,
        ...(held.zoom !== undefined ? { zoom: held.zoom } : {}),
      });
    }
  }
  if (filled.length === 0) return keyframes;
  return [...keyframes, ...filled].sort((a, b) => a.time - b.time);
}
